import { Dialog as DialogPrimitive } from '@base-ui/react/dialog';
import { useRender } from '@base-ui/react/use-render';
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { CloseButton } from '@/components/ui/close-button';
import { cn } from '@/lib/utils';

const ModalContext = React.createContext(true);

function Dialog({ modal = true, ...props }: DialogPrimitive.Root.Props) {
  return (
    <ModalContext.Provider value={modal === true}>
      <DialogPrimitive.Root data-slot="dialog" modal={modal} {...props} />
    </ModalContext.Provider>
  );
}

function DialogTrigger({ ...props }: DialogPrimitive.Trigger.Props) {
  return <DialogPrimitive.Trigger data-slot="dialog-trigger" {...props} />;
}

function DialogPortal({ ...props }: DialogPrimitive.Portal.Props) {
  return <DialogPrimitive.Portal data-slot="dialog-portal" {...props} />;
}

function DialogClose({ ...props }: DialogPrimitive.Close.Props) {
  return <DialogPrimitive.Close data-slot="dialog-close" {...props} />;
}

export interface DialogOverlayProps extends DialogPrimitive.Backdrop.Props {
  /** Disables open/close animations */
  disableAnimation?: boolean;
  /** Disables backdrop blur effect */
  disableBackdropBlur?: boolean;
}

function DialogOverlay({
  className,
  disableAnimation,
  disableBackdropBlur,
  ...props
}: DialogOverlayProps) {
  return (
    <DialogPrimitive.Backdrop
      data-slot="dialog-overlay"
      className={cn(
        'bg-foreground/10 fixed inset-0 isolate z-50',
        !disableAnimation &&
          'motion-reduce:transition-none motion-safe:transition-opacity motion-safe:duration-(--duration-moderate) motion-safe:ease-decelerate motion-safe:data-starting-style:opacity-0 motion-safe:data-ending-style:opacity-0 motion-safe:data-ending-style:duration-(--duration-fast) motion-safe:data-ending-style:ease-standard',
        !disableBackdropBlur && 'supports-backdrop-filter:backdrop-blur-xs',
        className,
      )}
      {...props}
    />
  );
}

export interface DialogContentProps extends DialogPrimitive.Popup.Props {
  showCloseButton?: boolean;
  /** Controls focus behavior when dialog opens. Set to false to prevent auto-focus. */
  initialFocus?: boolean | React.RefObject<HTMLElement | null>;
  /** Disables open/close animations */
  disableAnimation?: boolean;
  /** Disables backdrop blur effect */
  disableBackdropBlur?: boolean;
}

// Base UI retains exiting popups for CSS motion. Native inert excludes their
// descendants from pointer and keyboard interaction without stopping departure.
function DialogPopupSurface({
  surfaceProps,
  state,
  render,
}: {
  surfaceProps: React.HTMLAttributes<HTMLElement>;
  state: DialogPrimitive.Popup.State;
  render: DialogPrimitive.Popup.Props['render'];
}) {
  const element = useRender({
    render:
      typeof render === 'function' ? (props) => render(props, state) : render,
    props: { ...surfaceProps },
  });
  // Base UI merges render-element props last. Apply exit inertness only after
  // that merge, preserving the final children, consumer refs and open inertness.
  const props = element.props as React.HTMLAttributes<HTMLElement> &
    React.RefAttributes<HTMLElement>;
  const ref =
    Number.parseInt(React.version, 10) >= 19
      ? props.ref
      : (element as React.ReactElement & React.RefAttributes<HTMLElement>).ref;
  return React.createElement(element.type, {
    ...props,
    key: element.key,
    ref,
    inert: !state.open || props.inert,
  });
}

const DialogPopup = React.forwardRef<
  HTMLDivElement,
  DialogPrimitive.Popup.Props
>(function DialogPopup({ render, ...props }, ref) {
  return (
    <DialogPrimitive.Popup
      ref={ref}
      data-slot="dialog-popup"
      {...props}
      render={(surfaceProps, state) => (
        <DialogPopupSurface
          surfaceProps={surfaceProps}
          state={state}
          render={render}
        />
      )}
    />
  );
});
DialogPopup.displayName = 'Dialog.Popup';
DialogPopupSurface.displayName = 'Dialog.PopupSurface';

function DialogContent({
  className,
  children,
  showCloseButton = true,
  initialFocus,
  disableAnimation,
  disableBackdropBlur,
  ...props
}: DialogContentProps) {
  return (
    <DialogPortal>
      <DialogOverlay
        disableAnimation={disableAnimation}
        disableBackdropBlur={disableBackdropBlur}
      />
      <DialogPopup
        data-slot="dialog-content"
        aria-modal={React.useContext(ModalContext) ? true : undefined}
        initialFocus={initialFocus}
        className={cn(
          'bg-background ring-foreground/10 grid max-w-[calc(100%-2rem)] gap-6 rounded-xl p-6 text-sm ring-1 sm:max-w-md fixed top-1/2 left-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2 outline-none',
          !disableAnimation &&
            'motion-reduce:transition-none motion-safe:transition-[opacity,transform] motion-safe:duration-(--duration-moderate) motion-safe:ease-decelerate motion-safe:data-starting-style:opacity-0 motion-safe:data-starting-style:[transform:scale(0.98)] motion-safe:data-ending-style:opacity-0 motion-safe:data-ending-style:[transform:scale(0.98)] motion-safe:data-ending-style:duration-(--duration-fast) motion-safe:data-ending-style:ease-standard',
          className,
        )}
        {...props}
      >
        {children}
        {showCloseButton && (
          <DialogPrimitive.Close
            render={<CloseButton className="absolute top-4 end-4" />}
          />
        )}
      </DialogPopup>
    </DialogPortal>
  );
}

function DialogHeader({ className, ...props }: React.ComponentProps<'div'>) {
  return (
    <div
      data-slot="dialog-header"
      className={cn('gap-2 flex flex-col', className)}
      {...props}
    />
  );
}

function DialogFooter({
  className,
  showCloseButton = false,
  children,
  ...props
}: React.ComponentProps<'div'> & {
  showCloseButton?: boolean;
}) {
  return (
    <div
      data-slot="dialog-footer"
      className={cn(
        'gap-2 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end',
        className,
      )}
      {...props}
    >
      {children}
      {showCloseButton && (
        <DialogPrimitive.Close
          render={<Button appearance="outline" variant="secondary" />}
        >
          Close
        </DialogPrimitive.Close>
      )}
    </div>
  );
}

function DialogTitle({ className, ...props }: DialogPrimitive.Title.Props) {
  return (
    <DialogPrimitive.Title
      data-slot="dialog-title"
      className={cn('leading-none font-medium', className)}
      {...props}
    />
  );
}

function DialogDescription({
  className,
  ...props
}: DialogPrimitive.Description.Props) {
  return (
    <DialogPrimitive.Description
      data-slot="dialog-description"
      className={cn(
        'text-muted-foreground *:[a]:hover:text-foreground text-sm *:[a]:underline *:[a]:underline-offset-3',
        className,
      )}
      {...props}
    />
  );
}

DialogTrigger.displayName = 'Dialog.Trigger';
DialogPortal.displayName = 'Dialog.Portal';
DialogClose.displayName = 'Dialog.Close';
DialogOverlay.displayName = 'Dialog.Overlay';
DialogContent.displayName = 'Dialog.Content';
DialogHeader.displayName = 'Dialog.Header';
DialogFooter.displayName = 'Dialog.Footer';
DialogTitle.displayName = 'Dialog.Title';
DialogDescription.displayName = 'Dialog.Description';

Dialog.displayName = 'Dialog';

const DialogNamespace = Object.assign(Dialog, {
  Trigger: DialogTrigger,
  Portal: DialogPortal,
  Close: DialogClose,
  Overlay: DialogOverlay,
  Content: DialogContent,
  Header: DialogHeader,
  Footer: DialogFooter,
  Title: DialogTitle,
  Description: DialogDescription,
});

export {
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogNamespace as Dialog,
  DialogOverlay,
  DialogPopup,
  DialogPortal,
  DialogTitle,
  DialogTrigger,
};
